import { AxiosError } from 'axios'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { ErrorEnumMessage } from 'core/api/constants/error_messages'
import get from 'lodash/get'
import set from 'lodash/set'
import {
  useMutateCreateFeedPrograms,
  useMutateDeleteFeedProgram,
  useMutateEditFeedPrograms,
} from 'modules/pond/api/mutation'
import { useGetFeedProgramsById } from 'modules/pond/api/queries'
import { ProgramDetail } from 'modules/pond/api/types'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo } from 'react'
import { Controller, FieldErrors, Resolver, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'

import { Icon } from '@iconify/react'

// Backend limits — see the DOF-1292 API spec.
const NAME_MAX_LENGTH = 50
const MIN_ROWS = 2
const MAX_ROWS = 30
const MAX_WEIGHT_G = 500
const MAX_FEED_PERCENTAGE = 100

interface FormulaRow {
  shrimpWeightG: string
  feedPercentage: string
}

interface FeedProgramFormData {
  programName: string
  programDetails: FormulaRow[]
}

const EMPTY_ROW: FormulaRow = { shrimpWeightG: '', feedPercentage: '' }

// Keeps digits and a single decimal point.
const sanitizeDecimal = (value: string) => {
  const cleaned = value.replace(/[^\d.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  return rest.length > 0 ? `${whole}.${rest.join('')}` : whole
}

const toRows = (details: ProgramDetail[] | undefined): FormulaRow[] =>
  details && details.length > 0
    ? details.map((d) => ({
        shrimpWeightG: `${d.shrimpWeightG}`,
        feedPercentage: `${d.feedPercentage}`,
      }))
    : [EMPTY_ROW]

interface FeedProgramDialogProps {
  open: boolean
  /** Formula to edit; omit to create a new one. */
  feedProgramId?: number
  onClose: () => void
  onCreated?: (feedProgramId: number) => void
  onDeleted?: (feedProgramId: number) => void
}

const FeedProgramDialog = ({
  open,
  feedProgramId,
  onClose,
  onCreated,
  onDeleted,
}: FeedProgramDialogProps) => {
  const { t } = useTranslation(['pond', 'common'])
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = feedProgramId !== undefined

  const { data: feedProgram, isFetching } = useGetFeedProgramsById(feedProgramId, open && isEdit)
  const createMutation = useMutateCreateFeedPrograms()
  const editMutation = useMutateEditFeedPrograms(feedProgramId ?? 0)
  const deleteMutation = useMutateDeleteFeedProgram()

  const schema = useMemo(() => {
    const decimalInRange = (max: number, rangeMessage: string) =>
      Yup.string()
        .required(t('pond:feedProgram.error.required'))
        .test('range', rangeMessage, (v) => {
          if (!v) return true // reported by `required`
          const n = Number(v)
          return Number.isFinite(n) && n > 0 && n <= max
        })

    return Yup.object().shape({
      programName: Yup.string()
        .trim()
        .required(t('pond:please.input.program.name'))
        .max(NAME_MAX_LENGTH, t('pond:feedProgram.error.name.max')),
      programDetails: Yup.array()
        .of(
          Yup.object().shape({
            shrimpWeightG: decimalInRange(MAX_WEIGHT_G, t('pond:feedProgram.error.weight.range'))
              // Duplicate weights would make the size → % interpolation ambiguous.
              .test('unique', t('pond:feedProgram.error.weight.duplicate'), (v, ctx) => {
                if (!v) return true
                const rows = (ctx.from?.[1]?.value?.programDetails ?? []) as FormulaRow[]
                return rows.filter((r) => Number(r.shrimpWeightG) === Number(v)).length <= 1
              }),
            feedPercentage: decimalInRange(
              MAX_FEED_PERCENTAGE,
              t('pond:feedProgram.error.percentage.range')
            ),
          })
        )
        .min(MIN_ROWS, t('pond:feedProgram.error.rows.min'))
        .max(MAX_ROWS, t('pond:feedProgram.error.rows.max')),
    })
  }, [t])

  // The shared useYupValidationResolver returns flat keys ("programDetails[0].shrimpWeightG"),
  // which useFieldArray can't read per row, so build nested errors here. Array-level errors
  // (row count) go under `.root` so they don't overwrite the per-row ones.
  const resolver: Resolver<FeedProgramFormData> = useCallback(
    async (data) => {
      try {
        const values = await schema.validate(data, { abortEarly: false })
        return { values: values as FeedProgramFormData, errors: {} }
      } catch (e) {
        const errors: FieldErrors<FeedProgramFormData> = {}
        ;(e as Yup.ValidationError).inner.forEach((err) => {
          if (!err.path) return
          const path = err.path === 'programDetails' ? 'programDetails.root' : err.path
          // A field can fail several tests; show the first (e.g. "required").
          if (get(errors, path)) return
          set(errors, path, { type: err.type ?? 'validation', message: err.message })
        })
        return { values: {}, errors }
      }
    },
    [schema]
  )

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<FeedProgramFormData>({
    defaultValues: { programName: '', programDetails: [EMPTY_ROW] },
    resolver,
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'programDetails' })
  // The row-count error lives under `.root`, which RHF doesn't revalidate on
  // append/remove, so re-run validation once the user has tried to save.
  const changeRows = (change: () => void) => {
    change()
    if (isSubmitted) trigger()
  }

  useEffect(() => {
    if (!open) return
    if (!isEdit) {
      reset({ programName: '', programDetails: [EMPTY_ROW] })
    } else if (feedProgram) {
      reset({
        programName: feedProgram.programName,
        programDetails: toRows(feedProgram.programDetails),
      })
    }
  }, [open, isEdit, feedProgram, reset])

  const toPayload = (data: FeedProgramFormData) => ({
    programName: data.programName.trim(),
    programDetails: data.programDetails.map((row) => ({
      shrimpWeightG: Number(row.shrimpWeightG),
      feedPercentage: Number(row.feedPercentage),
    })),
  })

  // 409s are already shown by the global axios interceptor; 400s carry a
  // validation code we can translate here.
  const showBadRequest = (error: unknown) => {
    const { response } = error as AxiosError<{ message?: string }>
    if (response?.status !== 400) return
    const code = `${response.data?.message ?? ''}`.split(':').pop()?.trim() ?? ''
    enqueueSnackbar(
      ErrorEnumMessage[code as keyof typeof ErrorEnumMessage] ??
        t('pond:feedProgram.error.generic'),
      { variant: 'error' }
    )
  }

  const saveEdit = async () => {
    try {
      await editMutation.mutateAsync({ feedProgramId, body: toPayload(getValues()) })
      enqueueSnackbar(t('common:save.success'), { variant: 'success' })
      onClose()
    } catch (error) {
      showBadRequest(error)
      throw error
    }
  }

  const deleteProgram = async () => {
    if (feedProgramId === undefined) return
    try {
      await deleteMutation.mutateAsync(feedProgramId)
    } catch (error) {
      showBadRequest(error)
      throw error
    }
    enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
    onDeleted?.(feedProgramId)
    onClose()
  }

  // Editing a formula changes the total yield of every cycle using it, so
  // the user confirms first.
  const editConfirm = useDeleteConfirmDialog({
    title: t('pond:feedProgram.confirm.edit.title'),
    content: t('pond:feedProgram.confirm.edit.content'),
    onConfirm: saveEdit,
  })
  const deleteConfirm = useDeleteConfirmDialog({
    title: t('pond:feedProgram.confirm.delete.title'),
    content: t('pond:feedProgram.confirm.delete.content', {
      name: feedProgram?.programName ?? '',
    }),
    onConfirm: deleteProgram,
    confirmTxt: t('pond:delete'),
  })

  const onSave = handleSubmit(async (data) => {
    if (isEdit) {
      editConfirm.openDialog()
      return
    }
    try {
      const res = await createMutation.mutateAsync({ body: toPayload(data) })
      enqueueSnackbar(t('common:create.success'), { variant: 'success' })
      onCreated?.(res.data.data.id)
      onClose()
    } catch (error) {
      showBadRequest(error)
    }
  })

  const rowsError = errors.programDetails?.root?.message
  const isLoadingDetail = isEdit && isFetching && !feedProgram

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h5">
              {isEdit ? t('pond:feedProgram.edit.title') : t('pond:feedProgram.create.title')}
            </Typography>
            {isEdit && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Icon icon="mdi:trash-can-outline" />}
                onClick={deleteConfirm.openDialog}
                disabled={isLoadingDetail}
              >
                {t('pond:feedProgram.delete')}
              </Button>
            )}
          </Stack>

          {isLoadingDetail ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={48} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Typography color="text.secondary">{t('pond:program.name')}</Typography>
                <Controller
                  name="programName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.programName}
                      helperText={errors.programName?.message}
                      inputProps={{ maxLength: NAME_MAX_LENGTH }}
                    />
                  )}
                />
              </Stack>

              <Stack spacing={1}>
                <Typography color="text.secondary">{t('pond:program')}</Typography>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ px: 2, py: 1.5, bgcolor: theme.palette.grey[100] }}
                  >
                    <Typography flex={1} variant="subtitle2">
                      {t('pond:feedProgram.weight')}
                    </Typography>
                    <Typography flex={1} variant="subtitle2">
                      {t('pond:feedProgram.feed.percentage')}
                    </Typography>
                    <Box width={40} />
                  </Stack>

                  {fields.map((row, index) => {
                    const rowErrors = errors.programDetails?.[index]
                    return (
                      <Stack
                        key={row.id}
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}
                      >
                        {(['shrimpWeightG', 'feedPercentage'] as const).map((key) => (
                          <Controller
                            key={key}
                            name={`programDetails.${index}.${key}`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                sx={{ flex: 1 }}
                                inputProps={{ inputMode: 'decimal' }}
                                onChange={(e) => field.onChange(sanitizeDecimal(e.target.value))}
                                error={!!rowErrors?.[key]}
                                helperText={rowErrors?.[key]?.message}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {key === 'shrimpWeightG' ? t('pond:feedProgram.gram') : '%'}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            )}
                          />
                        ))}
                        <Box width={40} display="flex" justifyContent="center" pt={1}>
                          {fields.length > 1 && (
                            <IconButton
                              color="error"
                              onClick={() => changeRows(() => remove(index))}
                            >
                              <Icon icon="mdi:close" />
                            </IconButton>
                          )}
                        </Box>
                      </Stack>
                    )
                  })}

                  {fields.length < MAX_ROWS && (
                    <Box sx={{ px: 1, py: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Button
                        startIcon={<Icon icon="mdi:plus" />}
                        onClick={() => changeRows(() => append(EMPTY_ROW))}
                      >
                        {t('pond:add')}
                      </Button>
                    </Box>
                  )}
                </Box>
                {rowsError && (
                  <Typography color="error" variant="caption">
                    {rowsError}
                  </Typography>
                )}
              </Stack>
            </Stack>
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button fullWidth size="large" variant="outlined" onClick={onClose}>
              {t('common:cancel')}
            </Button>
            <LoadingButton
              fullWidth
              size="large"
              variant="contained"
              onClick={onSave}
              loading={createMutation.isLoading}
              disabled={isLoadingDetail}
            >
              {t('common:save')}
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
      {editConfirm.dialog}
      {deleteConfirm.dialog}
    </>
  )
}

export default FeedProgramDialog
